import type {
  ReimbursementDocumentStatus,
  ReimbursementStatusBarProps,
} from "../types/components";
import type { ReimbursementSectionStatusValue } from "./reimbursementSectionStatus";

export type ReimbursementMilestoneStatus =
  | "complete"
  | "partial"
  | "pending"
  | "rejected";

export type ReimbursementMilestoneStatusInput = Pick<
  ReimbursementStatusBarProps,
  | "firstMonthRentUnpaid"
  | "reimbursementReqDate"
  | "reimbursementType"
  | "reason"
  | "signedLease"
  | "currentLedger"
  | "finalLedger"
  | "skipAffidavit"
  | "evictionLawsuit"
  | "alternateCourtDocument"
  | "caresActNotice"
  | "bankruptcyDocument"
  | "courtRegistry"
  | "stipulationAgreement"
  | "defaultStipulation"
  | "bondRegistryReceipt"
  | "caresActApplicable"
  | "bankruptcyApplicable"
  | "appealApplicable"
  | "stipulationApplicable"
  | "alternateCourtDocApplicable"
  | "usingThirdPartyEvictionServices"
  | "utilizingLegalTeamForEviction"
  | "thirdPartyServicerName"
  | "residentPaidIntoCourt"
  | "residentDefaultedOnRepayment"
  | "securityDepositCollected"
  | "securityDeposit"
  | "leaseModificationsAtMoveIn"
  | "skippedDuringEviction"
  | "originalLease"
  | "originalLedger"
  | "moveOutDate"
  | "newResidentLeaseStartDate"
  | "newResidentMoveInDate"
  | "paymentInfoConfirmed"
>;

export function isReviewIncompleteDocument(
  doc: ReimbursementDocumentStatus | undefined,
): boolean {
  if (!doc?.uploaded) return false;
  const status = doc.status;
  return status === "incomplete" || status === "rejected";
}

export function isReimbursementSectionEditable(
  sectionWorkflowStatus: ReimbursementSectionStatusValue | null | undefined,
): boolean {
  const status = sectionWorkflowStatus ?? null;
  return status !== "SUBMITTED" && status !== "APPROVED";
}

function coreHasReviewIncompleteDocument(
  input: ReimbursementMilestoneStatusInput,
): boolean {
  const { signedLease, currentLedger, leaseModificationsAtMoveIn, originalLease } =
    input;
  return (
    isReviewIncompleteDocument(signedLease) ||
    isReviewIncompleteDocument(currentLedger) ||
    (leaseModificationsAtMoveIn === true &&
      isReviewIncompleteDocument(originalLease))
  );
}

function uniqueHasReviewIncompleteDocument(
  input: ReimbursementMilestoneStatusInput,
): boolean {
  const {
    skippedDuringEviction,
    skipAffidavit,
    caresActNotice,
    evictionLawsuit,
    alternateCourtDocument,
    bankruptcyDocument,
    courtRegistry,
    stipulationAgreement,
    defaultStipulation,
    bondRegistryReceipt,
  } = input;

  if (skippedDuringEviction === true) {
    return (
      isReviewIncompleteDocument(skipAffidavit) ||
      isReviewIncompleteDocument(caresActNotice)
    );
  }

  return (
    isReviewIncompleteDocument(skipAffidavit) ||
    isReviewIncompleteDocument(evictionLawsuit) ||
    isReviewIncompleteDocument(alternateCourtDocument) ||
    isReviewIncompleteDocument(caresActNotice) ||
    isReviewIncompleteDocument(bankruptcyDocument) ||
    isReviewIncompleteDocument(courtRegistry) ||
    isReviewIncompleteDocument(stipulationAgreement) ||
    isReviewIncompleteDocument(defaultStipulation) ||
    isReviewIncompleteDocument(bondRegistryReceipt)
  );
}

function finalHasReviewIncompleteDocument(
  input: ReimbursementMilestoneStatusInput,
): boolean {
  const { finalLedger, leaseModificationsAtMoveIn, originalLedger } = input;
  return (
    isReviewIncompleteDocument(finalLedger) ||
    (leaseModificationsAtMoveIn === true &&
      isReviewIncompleteDocument(originalLedger))
  );
}

export function milestoneHasReviewIncompleteDocument(
  input: ReimbursementMilestoneStatusInput,
  milestoneId: number,
): boolean {
  if (milestoneId === 1) return coreHasReviewIncompleteDocument(input);
  if (milestoneId === 2) return uniqueHasReviewIncompleteDocument(input);
  if (milestoneId === 3) return finalHasReviewIncompleteDocument(input);
  return false;
}

/** Left-bar milestone chip: Action needed only when the section is editable and a doc was marked incomplete. */
export function getMilestoneDisplayStatus(
  input: ReimbursementMilestoneStatusInput,
  milestoneId: number,
  sectionWorkflowStatus: ReimbursementSectionStatusValue | null | undefined,
): ReimbursementMilestoneStatus {
  const base = getReimbursementMilestoneStatus(input, milestoneId);
  const actionNeeded =
    isReimbursementSectionEditable(sectionWorkflowStatus) &&
    milestoneHasReviewIncompleteDocument(input, milestoneId);
  if (actionNeeded) return "rejected";
  if (base === "rejected" && !isReimbursementSectionEditable(sectionWorkflowStatus)) {
    return "partial";
  }
  return base;
}

function isCoreDetailsComplete(input: ReimbursementMilestoneStatusInput): boolean {
  const {
    firstMonthRentUnpaid,
    reimbursementReqDate,
    reimbursementType,
    securityDepositCollected,
    securityDeposit,
    leaseModificationsAtMoveIn,
    originalLease,
    signedLease,
    currentLedger,
  } = input;

  return !!(
    firstMonthRentUnpaid &&
    reimbursementReqDate &&
    reimbursementType &&
    securityDepositCollected !== null &&
    (securityDepositCollected !== true || !!securityDeposit) &&
    leaseModificationsAtMoveIn !== null &&
    (leaseModificationsAtMoveIn === false ||
      (originalLease.uploaded && originalLease.status === "approved")) &&
    signedLease.uploaded &&
    signedLease.status === "approved" &&
    currentLedger.uploaded &&
    currentLedger.status === "approved"
  );
}

function isUniqueRequirementsComplete(
  input: ReimbursementMilestoneStatusInput,
): boolean {
  const {
    reimbursementType,
    reason,
    skipAffidavit,
    skippedDuringEviction,
    caresActApplicable,
    caresActNotice,
    evictionLawsuit,
    usingThirdPartyEvictionServices,
    utilizingLegalTeamForEviction,
    alternateCourtDocApplicable,
    alternateCourtDocument,
    bankruptcyApplicable,
    bankruptcyDocument,
    appealApplicable,
    courtRegistry,
    residentPaidIntoCourt,
    bondRegistryReceipt,
    stipulationApplicable,
    stipulationAgreement,
    residentDefaultedOnRepayment,
    defaultStipulation,
    thirdPartyServicerName,
  } = input;

  if (!reimbursementType || !reason) return false;

  if (reimbursementType === "Skipped") {
    return skipAffidavit.uploaded && skipAffidavit.status === "approved";
  }

  if (reimbursementType === "Eviction") {
    if (skippedDuringEviction === true) {
      let complete = caresActApplicable !== null;
      if (caresActApplicable === true) {
        complete =
          complete &&
          caresActNotice.uploaded &&
          caresActNotice.status === "approved";
      }
      complete =
        complete &&
        skipAffidavit.uploaded &&
        skipAffidavit.status === "approved";
      return complete;
    }

    let complete =
      evictionLawsuit.uploaded &&
      evictionLawsuit.status === "approved" &&
      usingThirdPartyEvictionServices !== null &&
      utilizingLegalTeamForEviction !== null &&
      caresActApplicable !== null &&
      bankruptcyApplicable !== null &&
      appealApplicable !== null &&
      stipulationApplicable !== null &&
      alternateCourtDocApplicable !== null;

    if (usingThirdPartyEvictionServices === true) {
      complete = complete && !!thirdPartyServicerName;
    }

    if (alternateCourtDocApplicable === true) {
      complete =
        complete &&
        alternateCourtDocument.uploaded &&
        alternateCourtDocument.status === "approved";
    }

    if (caresActApplicable === true) {
      complete =
        complete &&
        caresActNotice.uploaded &&
        caresActNotice.status === "approved";
    }

    if (bankruptcyApplicable === true) {
      complete =
        complete &&
        bankruptcyDocument.uploaded &&
        bankruptcyDocument.status === "approved";
    }

    if (appealApplicable === true) {
      complete =
        complete &&
        courtRegistry.uploaded &&
        courtRegistry.status === "approved" &&
        residentPaidIntoCourt !== null;
      if (residentPaidIntoCourt === true) {
        complete =
          complete &&
          bondRegistryReceipt.uploaded &&
          bondRegistryReceipt.status === "approved";
      }
    }

    if (stipulationApplicable === true) {
      complete =
        complete &&
        stipulationAgreement.uploaded &&
        stipulationAgreement.status === "approved" &&
        residentDefaultedOnRepayment !== null;
      if (residentDefaultedOnRepayment === true) {
        complete =
          complete &&
          defaultStipulation.uploaded &&
          defaultStipulation.status === "approved";
      }
    }

    return complete;
  }

  return false;
}

export function getReimbursementMilestoneStatus(
  input: ReimbursementMilestoneStatusInput,
  milestoneId: number,
): ReimbursementMilestoneStatus {
  const {
    firstMonthRentUnpaid,
    reimbursementReqDate,
    reimbursementType,
    reason,
    signedLease,
    currentLedger,
    leaseModificationsAtMoveIn,
    skippedDuringEviction,
    skipAffidavit,
    evictionLawsuit,
    alternateCourtDocument,
    caresActApplicable,
    alternateCourtDocApplicable,
    bankruptcyApplicable,
    appealApplicable,
    stipulationApplicable,
    usingThirdPartyEvictionServices,
    utilizingLegalTeamForEviction,
    finalLedger,
    originalLedger,
    moveOutDate,
    newResidentLeaseStartDate,
    newResidentMoveInDate,
    paymentInfoConfirmed,
    securityDepositCollected,
  } = input;

  if (milestoneId === 1) {
    if (coreHasReviewIncompleteDocument(input)) {
      return "rejected";
    }
    if (isCoreDetailsComplete(input)) return "complete";
    if (
      firstMonthRentUnpaid ||
      reimbursementReqDate ||
      reimbursementType ||
      securityDepositCollected !== null ||
      leaseModificationsAtMoveIn !== null ||
      signedLease.uploaded ||
      currentLedger.uploaded
    ) {
      return "partial";
    }
    return "pending";
  }

  if (milestoneId === 2) {
    if (!reimbursementType) return "pending";
    if (skippedDuringEviction === true) {
      if (uniqueHasReviewIncompleteDocument(input)) {
        return "rejected";
      }
      return isUniqueRequirementsComplete(input)
        ? "complete"
        : reason || skipAffidavit.uploaded || caresActApplicable !== null
          ? "partial"
          : "pending";
    }
    if (uniqueHasReviewIncompleteDocument(input)) {
      return "rejected";
    }

    return isUniqueRequirementsComplete(input)
      ? "complete"
      : reason ||
          skipAffidavit.uploaded ||
          evictionLawsuit.uploaded ||
          alternateCourtDocument.uploaded ||
          caresActApplicable !== null ||
          alternateCourtDocApplicable !== null ||
          bankruptcyApplicable !== null ||
          appealApplicable !== null ||
          stipulationApplicable !== null ||
          skippedDuringEviction !== null ||
          usingThirdPartyEvictionServices !== null ||
          utilizingLegalTeamForEviction !== null
        ? "partial"
        : "pending";
  }

  if (milestoneId === 3) {
    if (finalHasReviewIncompleteDocument(input)) {
      return "rejected";
    }
    if (!finalLedger.uploaded) return "pending";
    const originalLedgerOk =
      leaseModificationsAtMoveIn !== true ||
      (originalLedger.uploaded && originalLedger.status === "approved");
    if (
      finalLedger.status === "approved" &&
      originalLedgerOk &&
      moveOutDate &&
      newResidentLeaseStartDate &&
      newResidentMoveInDate &&
      paymentInfoConfirmed
    ) {
      return "complete";
    }
    return "partial";
  }

  return "pending";
}

export const REIMBURSEMENT_MILESTONE_LABELS: Record<1 | 2 | 3, string> = {
  1: "Core Information",
  2: "Unique Requirements",
  3: "Final Information",
};
